import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AnimatedCheckmark } from "@/components/ui/animated-checkmark";
import { useIsMobile } from "@/hooks/use-mobile";
import { AnimatedErrorAlert } from "@/components/ui/animated-error-alert";
import { useState, useEffect, useRef, useCallback } from "react";

interface ToastProps {
  variant?: "default" | "success" | "destructive";
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  onClose?: () => void;
  open?: boolean;
  duration?: number;
  className?: string;
}

const ToastImpl = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      className,
      variant = "default",
      title,
      description,
      icon,
      onClose,
      duration = 6000,
      ...props
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(true);
    const [isExiting, setIsExiting] = useState(false);
    const [isEntering, setIsEntering] = useState(true);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const isMobile = useIsMobile();

    const handleClose = useCallback(() => {
      setIsExiting(true);

      // Wait for CSS transition to complete before calling onClose
      const transitionTimeout = setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose();
      }, 300); // Matches CSS transition duration

      return () => clearTimeout(transitionTimeout);
    }, [onClose]);

    useEffect(() => {
      // Start enter animation
      const enterTimer = setTimeout(() => {
        setIsEntering(false);
      }, 50);

      if (duration !== Infinity) {
        timeoutRef.current = setTimeout(() => {
          handleClose();
        }, duration);
      }

      return () => {
        clearTimeout(enterTimer);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, [duration, handleClose]);

    if (!isVisible) return null;

    return (
      <div
        ref={ref}
        className={cn(
          "relative pointer-events-auto flex overflow-hidden shadow-lg",
          "items-start gap-4",
          isMobile ? "w-full py-4 px-4 rounded-b-lg" : "p-4 rounded-lg",
          "transition-all duration-300 ease-in-out",
          isEntering
            ? "opacity-0 translate-y-[-100px]"
            : "opacity-100 translate-y-0",
          isExiting ? "opacity-0 translate-y-[-100px]" : "",
          variant === "default" && "bg-primary text-primary-foreground",
          variant === "success" && "bg-green-600 text-white",
          variant === "destructive" &&
            "bg-destructive text-destructive-foreground",
          className
        )}
        {...props}
      >
        {/* Icon */}
        {icon && (
          <div className='flex-shrink-0 text-white flex items-center self-center'>
            {icon}
          </div>
        )}

        {/* Content */}
        <div className='flex-1 flex flex-col gap-1 items-start'>
          {title && (
            <h3 className='font-semibold leading-none tracking-tight'>
              {title}
            </h3>
          )}
          {description && <p className='opacity-90 text-sm'>{description}</p>}
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className='absolute right-2 top-2 inline-flex h-4 w-4 items-center justify-center rounded-md hover:bg-white/20 focus:outline-none'
        >
          <X className='h-3 w-3' />
        </button>
      </div>
    );
  }
);
ToastImpl.displayName = "ToastImpl";

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (props, ref) => {
    // Default icons based on variant
    const defaultIcon = React.useMemo(() => {
      switch (props.variant) {
        case "success":
          return <AnimatedCheckmark variant='inverted' size={32} />;
        case "destructive":
          return <AnimatedErrorAlert variant='error' />;
        default:
          return null;
      }
    }, [props.variant]);

    const finalProps = {
      ...props,
      icon: props.icon || defaultIcon,
      ref,
    };

    return <ToastImpl {...finalProps} />;
  }
);
Toast.displayName = "Toast";

export const ToastViewport = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const isMobile = useIsMobile();

  return (
    <div
      ref={ref}
      className={cn(
        "fixed",
        !isMobile &&
          "top-0 left-1/2 -translate-x-1/2 p-4 flex flex-col-reverse gap-2 z-[250] w-[600px] max-w-[calc(100%-2rem)]",
        isMobile && "inset-0 pointer-events-none",
        className
      )}
      {...props}
    />
  );
});
ToastViewport.displayName = "ToastViewport";
